import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Minus, Plus, Trash2, ArrowLeft, ShoppingBag, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, cartTotal, loading } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to checkout');
      navigate('/login');
      return;
    }

    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-concrete-white flex flex-col items-center justify-center px-6" data-testid="cart-page">
        <ShoppingBag className="w-16 h-16 text-cement-grey mb-4" />
        <h1 className="font-anton text-3xl tracking-tighter uppercase">SIGN IN TO VIEW CART</h1>
        <p className="text-cement-grey mt-2">Please login to access your shopping cart</p>
        <Link to="/login">
          <Button className="mt-6 bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-4 px-8 rounded-sm">
            Login
          </Button>
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-concrete-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-chicago-red border-t-transparent rounded-full" />
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-concrete-white flex flex-col items-center justify-center px-6" data-testid="cart-page">
        <ShoppingBag className="w-16 h-16 text-cement-grey mb-4" />
        <h1 className="font-anton text-3xl tracking-tighter uppercase">YOUR CART IS EMPTY</h1>
        <p className="text-cement-grey mt-2">Looks like you haven't added anything yet</p>
        <Link to="/products">
          <Button className="mt-6 bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-4 px-8 rounded-sm">
            Start Shopping
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-concrete-white" data-testid="cart-page">
      {/* Header */}
      <div className="bg-deep-obsidian py-12">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/products" className="inline-flex items-center text-cement-grey hover:text-white transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="uppercase text-xs tracking-widest font-bold">Continue Shopping</span>
          </Link>
          <h1 className="font-anton text-4xl md:text-5xl text-white tracking-tighter uppercase">YOUR CART</h1>
          <p className="text-cement-grey mt-2">{cart.items.length} items</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cart.items.map((item) => (
              <div
                key={`${item.product_id}-${item.selected_size}`}
                className="bg-white border border-gray-200 p-6 flex gap-6"
                data-testid={`cart-item-${item.product_id}`}
              >
                <div className="w-24 h-24 bg-gray-100 flex-shrink-0">
                  <img
                    src={item.product?.images?.[0] || 'https://via.placeholder.com/100'}
                    alt={item.product?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <div>
                      <p className="text-cement-grey text-xs uppercase tracking-wider">{item.product?.brand}</p>
                      <Link 
                        to={`/products/${item.product_id}`}
                        className="font-bold hover:text-chicago-red transition-colors"
                      >
                        {item.product?.name}
                      </Link>
                      <p className="text-cement-grey text-sm mt-1">Size: {item.selected_size}</p>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.product_id, item.selected_size)}
                      className="text-cement-grey hover:text-chicago-red transition-colors"
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-gray-200">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.selected_size, item.quantity - 1)}
                        className="p-2 hover:bg-gray-100 transition-colors"
                        data-testid={`decrease-qty-${item.product_id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-12 text-center font-bold">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.selected_size, item.quantity + 1)}
                        className="p-2 hover:bg-gray-100 transition-colors"
                        data-testid={`increase-qty-${item.product_id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <span className="font-bebas text-xl">${(item.product?.price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 p-6 sticky top-24">
              <h2 className="font-bebas text-xl tracking-wider mb-6">ORDER SUMMARY</h2>
              
              <div className="space-y-4 pb-6 border-b border-gray-200">
                <div className="flex justify-between">
                  <span className="text-cement-grey">Subtotal</span>
                  <span className="font-bold">${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-cement-grey">Shipping</span>
                  <span className="font-bold">Calculated at checkout</span>
                </div>
              </div>

              <div className="flex justify-between py-6 border-b border-gray-200">
                <span className="font-bebas text-xl">TOTAL</span>
                <span className="font-bebas text-2xl">${cartTotal.toFixed(2)}</span>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="w-full mt-6 bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-6 rounded-sm btn-primary"
                data-testid="checkout-btn"
              >
                {checkoutLoading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Proceed to Checkout
                  </>
                )}
              </Button>

              <p className="text-cement-grey text-xs text-center mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
